import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Table, Modal } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { cachedRequest, cacheWithTTL, getCachedData } from '../utils/apiClient';
import axios from 'axios';

const AdminManagers = () => {
  // Проверяем, какой токен используется
  const restaurantToken = localStorage.getItem('restaurantToken');
  const managerToken = localStorage.getItem('managerToken');
  
  // Если пользователь авторизован как менеджер, перенаправляем на правильную страницу
  if (managerToken && !restaurantToken) {
    return <Navigate to="/manager/waiters" replace />;
  }
  
  // Если нет ни одного токена, перенаправляем на страницу входа
  if (!restaurantToken && !managerToken) {
    return <Navigate to="/login" replace />;
  }

  const [form, setForm] = useState({ name: '', phone: '', password: '', role: 'administrator' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Managers list and permission updates
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true); // Начинаем с true для показа загрузки
  const [permError, setPermError] = useState('');
  const [permSuccess, setPermSuccess] = useState('');
  const [lastLoadTime, setLastLoadTime] = useState(null); // Для кэширования

  // Delete manager modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [managerToDelete, setManagerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);


  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('restaurantToken');
      if (!token) {
        setError('Вы не авторизованы как владелец ресторана.');
        setLoading(false);
        return;
      }

      console.log('Creating manager with data:', form);
      
      const response = await axios.post('/api/manager/register', form, {
        headers: { 'x-auth-token': token }
      });

      setSuccess('Администратор успешно создан.');
      setForm({ name: '', phone: '', password: '', role: 'administrator' });
      // reload list с принудительным обновлением
      await loadManagers(true);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Не удалось создать менеджера';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = useCallback(async (forceRefresh = false) => {
    try {
      setPermError('');
      setPermSuccess('');
      
      // Проверяем кэш (5 минут)
      const cacheKey = 'managers_cache';
      const cachedManagers = getCachedData(cacheKey);
      
      if (!forceRefresh && cachedManagers && managers.length > 0) {
        return;
      }
      
      setLoadingManagers(true);
      
      const token = localStorage.getItem('restaurantToken');
      if (!token) {
        setPermError('Вы не авторизованы как владелец ресторана.');
        setLoadingManagers(false);
        return;
      }
      
      // Используем кэшированный запрос
      const res = await cachedRequest('/api/manager', {
        headers: { 'x-auth-token': token },
        timeout: 10000 // 10 секунд таймаут
      });
      
      setManagers(res.data || []);
      cacheWithTTL(cacheKey, res.data || [], 5 * 60 * 1000); // 5 минут кэш
    } catch (err) {
      let msg = 'Не удалось загрузить список менеджеров';
      if (err.code === 'ECONNABORTED') {
        msg = 'Превышено время ожидания. Проверьте подключение к интернету.';
      } else if (err.response?.status === 401) {
        msg = 'Сессия истекла. Пожалуйста, войдите заново.';
      } else if (err.response?.status === 403) {
        msg = 'Недостаточно прав для просмотра списка менеджеров.';
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      
      setPermError(msg);
    } finally {
      setLoadingManagers(false);
    }
  }, [managers.length]);

  useEffect(() => {
    loadManagers();
  }, [loadManagers]);

  const updatePermission = async (managerId, canManageMenu) => {
    try {
      setPermError('');
      setPermSuccess('');
      const token = localStorage.getItem('restaurantToken');
      if (!token) {
        setPermError('Вы не авторизованы как владелец ресторана.');
        return;
      }
      
      // Определяем роль на основе прав
      const newRole = canManageMenu ? 'administrator' : 'waiter';
      
      await axios.put(`/api/manager/${managerId}/permissions`, { 
        canManageMenu, 
        role: newRole 
      }, {
        headers: { 'x-auth-token': token }
      });
      
      setPermSuccess('Права и роль менеджера обновлены');
      
      // Update local state
      setManagers((prev) =>
        prev.map((m) => (m._id === managerId ? { 
          ...m, 
          canManageMenu, 
          role: newRole 
        } : m))
      );
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Не удалось обновить права менеджера';
      setPermError(msg);
    }
  };

  // Delete manager functions
  const handleDeleteClick = (manager) => {
    setManagerToDelete(manager);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!managerToDelete) return;
    
    try {
      setDeleting(true);
      setPermError('');
      setPermSuccess('');
      
      const token = localStorage.getItem('restaurantToken');

      if (!token) {
        setPermError('Вы не авторизованы как владелец ресторана.');
        setDeleting(false);
        return;
      }

      const response = await axios.delete(`/api/manager/${managerToDelete._id}`, {
        headers: { 'x-auth-token': token }
      });

    

      setPermSuccess(`${managerToDelete.phone} успешно удален`);
      
      // Update local state
      setManagers((prev) => prev.filter((m) => m._id !== managerToDelete._id));
      
      // Close modal
      setShowDeleteModal(false);
      setManagerToDelete(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось удалить менеджера';
      setPermError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setManagerToDelete(null);
  };

  // Load data on component mount
  useEffect(() => {
    loadManagers();
  }, [loadManagers]);

  return (
    <Container className="mt-5">
      <Row className="mb-4">
        <Col>
          <h1>Управление пользователями</h1>
          <p>Создавайте учетные записи администраторов и официантов для управления заказами и столами.</p>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Body>
              <Card.Title>Добавить пользователя</Card.Title>
              {success && <Alert variant="success">{success}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}
              <Form onSubmit={onSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Имя пользователя (необязательно)</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Например: Иван Петров"
                  />
                  <Form.Text className="text-muted">
                    Если не указано, будет использовано "Администратор + номер телефона" или "Официант + номер телефона"
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Номер телефона</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+375291234567"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Пароль</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={onChange}
                    placeholder="Минимум 6 символов"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Роль</Form.Label>
                  <Form.Select
                    name="role"
                    value={form.role}
                    onChange={onChange}
                  >
                    <option value="administrator">Администратор</option>
                    <option value="waiter">Официант</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Администратор может управлять меню и создавать официантов
                  </Form.Text>
                </Form.Group>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? 'Создание...' : 'Создать пользователя'}
                </Button>
              </Form>
              <hr />
              <p className="mb-0">
                После создания пользователь может войти на странице Вход менеджера и управлять заказами.
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Список пользователей</Card.Title>
              {permSuccess && <Alert variant="success">{permSuccess}</Alert>}
              {permError && <Alert variant="danger">{permError}</Alert>}
              {loadingManagers ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                  </div>
                  <div className="text-muted mt-2">Загрузка списка менеджеров...</div>
                </div>
              ) : managers.length === 0 ? (
                <div className="text-muted text-center py-4">
                  <div className="mb-2">📋</div>
                  <div>Менеджеры не найдены</div>
                  <small>Создайте первого менеджера с помощью формы слева</small>
                </div>
              ) : (
                <Table bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Имя</th>
                      <th>Номер телефона</th>
                      <th>Права</th>
                      <th>Создан</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((m) => (
                      <tr key={m._id}>
                        <td>
                          {m.name && !m.name.startsWith('Администратор ') && !m.name.startsWith('Официант ') ? m.name : '—'}
                        </td>
                        <td>{m.phone}</td>
                        <td>
                          <div className="mb-2">
                            <small className="text-muted">
                              Роль: <strong>{m.role === 'administrator' ? 'Администратор' : 'Официант'}</strong>
                            </small>
                          </div>
                          <Form.Check
                            type="switch"
                            id={`toggle-${m._id}`}
                            label="Может управлять меню"
                            checked={!!m.canManageMenu}
                            onChange={(e) => updatePermission(m._id, e.target.checked)}
                          />
                          {m.role === 'administrator' && (
                            <small className="text-muted d-block">Администратор - может управлять правами других пользователей</small>
                          )}
                        </td>
                        <td>{new Date(m.createdAt).toLocaleString()}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(m)}
                            title="Удалить менеджера"
                          >
                            🗑️ Удалить
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              <div className="mt-2">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => loadManagers(true)}
                  disabled={loadingManagers}
                >
                  {loadingManagers ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Загрузка...
                    </>
                  ) : (
                    '🔄 Обновить'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal для подтверждения удаления менеджера */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {managerToDelete && (
            <div>
              <p>
                <strong>Вы уверены, что хотите удалить менеджера?</strong>
              </p>
              <div className="bg-light p-3 rounded">
                <p className="mb-1"><strong>Имя:</strong> {managerToDelete.name}</p>
                <p className="mb-1"><strong>Телефон:</strong> {managerToDelete.phone}</p>
                <p className="mb-1"><strong>Роль:</strong> {managerToDelete.role === 'administrator' ? 'Администратор' : 'Официант'}</p>
                <p className="mb-0"><strong>Права:</strong> {managerToDelete.canManageMenu ? 'Может управлять меню' : 'Только просмотр заказов'}</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={deleting}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Удаление...' : '🗑️ Удалить менеджера'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminManagers;